import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// ADDED: Import package management functions.
import { 
  validateScanAvailability, 
  incrementScanUsage 
} from '../utils/packageManagement';

import { sendInvitationEmails } from '../services/emailService';

import './TeamCreation.css';

const TeamCreation = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  // Main state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Team data
  const [teamData, setTeamData] = useState({
    name: '',
    description: ''
  });
  
  // Members
  const [members, setMembers] = useState([{ name: '', email: '' }]);
  const [emailErrors, setEmailErrors] = useState([false]); // true = invalid, false = valid
  const [createdTeam, setCreatedTeam] = useState(null);
  const [emailStatus, setEmailStatus] = useState('pending');

  // ADDED: Package validation state
  const [packageValidation, setPackageValidation] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);

  // ADDED: Load package validation on component mount
  useEffect(() => {
    if (user?.email) {
      loadPackageValidation();
    }
  }, [user]);

  // ADDED: Package validation loader
  const loadPackageValidation = async () => {
    try {
      setPackageLoading(true);
      const validation = await validateScanAvailability(user.email);
      setPackageValidation(validation);
      console.log('📦 Package validation loaded:', validation);
    } catch (error) {
      console.error('Error loading package validation:', error);
      setPackageValidation({
        canScan: false,
        message: 'Fout bij laden scan status',
        scansLeft: 0
      });
    } finally {
      setPackageLoading(false);
    }
  };

  // Form validation
  const validateStep1 = () => {
    if (!teamData.name.trim()) {
      setError('Relatienaam is verplicht');
      return false;
    }
    if (teamData.name.trim().length < 2) {
      setError('Relatienaam moet minimaal 2 karakters lang zijn');
      return false;
    }
    setError('');
    return true;
  };

 const validateStep2 = () => {
  // Check naam EN email per member
  const validMembers = members.filter(m => 
    m.name.trim() && 
    m.email.trim() && 
    isValidEmail(m.email.trim())
  );
  
  if (validMembers.length < 2) {
    setError('Voeg minimaal twee deelnemers toe met naam en geldig emailadres voor een GezinsScan');
    return false;
  }
  
  // Check max 15 deelnemers
  if (members.length > 15) {
    setError('Maximaal 15 deelnemers per gezin toegestaan');
    return false;
  }
  
  setError('');
  return true;
};

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const generateInviteCode = () => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  // Step navigation
  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
      setError('');
    } else {
      submitTeam();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  // Member management
 const addMember = () => {
  if (members.length >= 15) {
    setError('Maximaal 15 deelnemers per gezin toegestaan');
    return;
  }
  setMembers([...members, { name: '', email: '' }]);
  setEmailErrors([...emailErrors, false]);
  setError('');
};

const updateMember = (index, field, value) => {
  const newMembers = [...members];
  newMembers[index] = { ...newMembers[index], [field]: value };
  setMembers(newMembers);
  
  // Check alleen email
  if (field === 'email') {
    const errors = [...emailErrors];
    errors[index] = value.trim() !== '' && !isValidEmail(value.trim());
    setEmailErrors(errors);
  }
};

 const removeMember = (index) => {
  if (members.length > 1) {
    const newMembers = members.filter((_, i) => i !== index);
    const newErrors = emailErrors.filter((_, i) => i !== index);
    setMembers(newMembers);
    setEmailErrors(newErrors);
  }
};

  // UPDATED: Team submission with package management
  const submitTeam = async () => {
    setIsSubmitting(true);
    setError('');
    
    try {
      // ADDED: Pre-flight package validation
      console.log('📦 Validating scan availability...');
      const currentValidation = await validateScanAvailability(user.email);
      
      if (!currentValidation.canScan) {
        setError(currentValidation.message);
        setIsSubmitting(false);
        return;
      }

      // Filter valid emails
     const validMembers = members.filter(m => 
  m.name.trim() && 
  m.email.trim() && 
  isValidEmail(m.email.trim())
);
      
      console.log('Creating team with organization filtering...');
      console.log('Current user profile:', userProfile);
      console.log('Valid members:', validMembers);

      const organizationId = userProfile?.organizationId || 'default';

      // Create team document
      const teamDoc = {
        name: teamData.name.trim(),
        description: teamData.description.trim(),
        createdBy: user.displayName || user.email,
        createdByEmail: user.email,
        organizationId: organizationId,
        createdAt: serverTimestamp(),
        status: 'active',
       memberCount: validMembers.length
      };

      console.log('Creating team with data:', teamDoc);
      const docRef = await addDoc(collection(db, 'teams'), teamDoc);
      console.log('Team created with ID:', docRef.id);

      // Create invitations in subcollection + index
      const invitations = [];

      for (const member of validMembers) {
  const inviteCode = generateInviteCode();
  
  // Add to teams/{teamId}/invites subcollection
  const inviteRef = await addDoc(collection(db, 'teams', docRef.id, 'invites'), {
    name: member.name.trim(),
    email: member.email.trim(),
    code: inviteCode,
    used: false,
    createdAt: serverTimestamp()
  });

        // Add to index for fast lookup
      await addDoc(collection(db, 'invitations_index'), {
  code: inviteCode,
  teamId: docRef.id,
  inviteId: inviteRef.id,
  name: member.name.trim(),
  email: member.email.trim(),
  createdAt: serverTimestamp()
});

       invitations.push({
  name: member.name.trim(),
  email: member.email.trim(),
  code: inviteCode,
  id: inviteRef.id
});

       console.log('✅ Invitation created in subcollection + index:', { 
  name: member.name.trim(),
  email: member.email.trim(), 
  code: inviteCode,
  teamId: docRef.id 
});
      }

      console.log('All invitations created:', invitations);

      // ADDED: Increment package usage after successful team creation
      try {
        console.log('📦 Incrementing scan usage...');
        const usageResult = await incrementScanUsage(user.email);
        
        if (usageResult.success) {
          console.log('✅ Scan usage incremented:', usageResult.message);
        } else {
          console.error('❌ Scan usage increment failed:', usageResult.message);
          // Note: Team is already created, so we log but don't fail
        }
      } catch (packageError) {
        console.error('❌ Error incrementing scan usage:', packageError);
        // Team creation succeeded, package update failed - continue with flow
      }

// Send invitation emails via direct HTTP call
      try {
        console.log('📧 Sending invitation emails via HTTP...');
        
        const emailData = invitations.map(inv => ({
          email: inv.email,
          code: inv.code,
          name: inv.name  // âœ… TOEGEVOEGD - naam meesturen voor persoonlijke aanhef
        }));

        const teamInfo = {
          teamName: teamData.name,
          teamId: docRef.id,
          createdBy: user.displayName || user.email,
          organizationId: organizationId
        };

        console.log('📤 Sending emails for:', emailData.length, 'invitations');

        const emailResult = await sendInvitationEmails(emailData, teamInfo);

        if (emailResult.success) {
          console.log('✅ Emails sent successfully:', emailResult.totalSent);
          setEmailStatus('success');
        } else {
          console.error('⚠️ Some emails failed:', emailResult);
          setEmailStatus(emailResult.totalSent > 0 ? 'partial' : 'warning');
        }
      } catch (emailError) {
        console.error('❌ Email sending failed:', emailError);
        setEmailStatus('warning');
      }

      console.log('Team creation completed successfully');
      
      setCreatedTeam({
        id: docRef.id,
        name: teamData.name,
       memberCount: validMembers.length
      });

      setCurrentStep(3); // Success step
      
      // Dispatch event for Dashboard to refresh
      window.dispatchEvent(new CustomEvent('teamCreated'));
      
    } catch (error) {
      console.error('Error creating team:', error);
      setError('Er is een fout opgetreden bij het aanmaken van de relatie. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ADDED: Package status indicator component
  const renderPackageStatus = () => {
    if (packageLoading) {
      return (
        <div className="package-status-bar package-status-loading">
          <span className="loading-spinner"></span>
          <span>Package status laden...</span>
        </div>
      );
    }

    if (!packageValidation?.canScan) {
      return (
        <div className="package-status-bar package-status-error">
          <span className="package-icon">⚠️</span>
          <div className="package-text">
            <strong>Geen scans beschikbaar</strong>
            <p>{packageValidation?.message || 'Neem contact op met diederik@tractum.nl'}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="package-status-bar package-status-success">
        <span className="package-icon">✅</span>
        <div className="package-text">
          <strong>{packageValidation.scansLeft} scan(s) beschikbaar</strong>
          <p>Na aanmaken blijven er {packageValidation.scansLeft - 1} over</p>
        </div>
      </div>
    );
  };

  return (
    <div className="team-creation-container">
      <div className="team-creation-card">
        <div className="team-creation-header">
          <h1> Relatie Aanmaken </h1>
          <div className="progress-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <span className="step-number"> 1 </span>
              <span className="step-label"> Relatie Info </span>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <span className="step-number"> 2 </span>
              <span className="step-label"> Deelnemers </span>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <span className="step-number"> 3 </span>
              <span className="step-label"> Voltooid </span>
            </div>
          </div>
        </div>

        {/* ADDED: Package Status Bar */}
        {renderPackageStatus()}

        <div className="team-creation-content">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {/* Step 1: Team Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <h2>Relatie Informatie</h2>
              <div className="form-group">
                <label htmlFor="teamName">Relatienaam *</label>
                <input
                  id="teamName"
                  type="text"
                  value={teamData.name}
                  onChange={(e) => setTeamData({ ...teamData, name: e.target.value })}
                  placeholder="Voer de relatienaam in, minimaal 2 karakters"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="teamDescription">Beschrijving (optioneel)</label>
                <textarea
                  id="teamDescription"
                  value={teamData.description}
                  onChange={(e) => setTeamData({ ...teamData, description: e.target.value })}
                  placeholder="Voer een korte beschrijving in, bv de datum zodat je later makkelijk twee GezinsScans van dit gezin kunt vergelijken"
                  className="form-textarea"
                  rows={3}
                />
              </div>
              <div className="button-group">
                <button 
                  onClick={nextStep} 
                  className="btn-primary"
                  disabled={!packageValidation?.canScan}
                >
                  Volgende
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Team Members */}
          {currentStep === 2 && (
            <div className="form-step">
              <h2>Deelnemers uitnodigen</h2>
              <p className="step-description">
                Voeg de emailadressen van de deelnemers toe. Zij ontvangen een uitnodiging om deel te nemen aan de GezinsScan.
              </p>
              <div className="info-box">
  <span className="info-icon">ℹ️</span>
  <span>Voor een relatie scan zijn minimaal 2 deelnemers vereist</span>
</div>
              
             <div className="members-list">
  {members.map((member, index) => (
    <div key={index} className="member-input-group">
      <input
        type="text"
        value={member.name}
        onChange={(e) => updateMember(index, 'name', e.target.value)}
        placeholder="Naam (bijv. Jan)"
        className="form-input member-input"
      />
    <div className="email-field">
  <input
    type="email"
    value={member.email}
    onChange={(e) => updateMember(index, 'email', e.target.value)}
    placeholder="deelnemer@mail.nl"
    className={`form-input member-input ${emailErrors[index] ? 'input-error' : ''}`}
  />
  {emailErrors[index] && (
    <span className="error-text">Ongeldig emailadres</span>
  )}
</div>
      {members.length > 1 && (
        <button
          type="button"
          onClick={() => removeMember(index)}
          className="btn-remove"
        >
          ×
        </button>
      )}
    </div>
  ))}
</div>

              <button
                type="button"
                onClick={addMember}
                className="btn-secondary add-member-btn"
              >
                + Nog een deelnemer toevoegen
              </button>

              <div className="button-group">
                <button onClick={prevStep} className="btn-secondary">
                  Vorige
                </button>
               <button 
  onClick={nextStep} 
  className="btn-primary"
  disabled={isSubmitting || !packageValidation?.canScan || emailErrors.some(e => e === true)}
>
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Relatie Aanmaken...
                    </>
                  ) : (
                    'Relatie Aanmaken'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <div className="form-step success-step">
              <div className="success-container">
                <div className="success-icon">✅</div>
                <h2>Gezin succesvol aangemaakt!</h2>
                
                <div className="success-details">
                  <p><strong>Relatie:</strong> {createdTeam?.name}</p>
                  <p><strong>Deelnemers:</strong> {createdTeam?.memberCount}</p>
                  <p><strong>Status:</strong> {
                    emailStatus === 'success' ? '✅ Uitnodigingen verzonden!' :
                    emailStatus === 'warning' ? '⚠️ Gezin aangemaakt, emails volgen later' :
                    '⏳ Emails worden verwerkt...'
                  }</p>
                </div>

                <div className="button-group">
                  <button 
                    onClick={() => navigate('/dashboard')}
                    className="btn-primary"
                  >
                    Naar Dashboard
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamCreation;